import type { ISODate, Taak, Takenpakket, Werkpakket } from './types';

export type PakketStatus = 'Open' | 'Wacht' | 'Bezig' | 'Ingepland' | 'Te laat';

export interface PakketVoortgang {
  status: PakketStatus;
  label: string;
  uren: number;
  ingepland: number;
  wachtOp?: string;
}

function urenVanTaak(t: Taak): number {
  // focusblokken (SPEC §9.7) houden alleen de agenda vrij; de uren tellen via de sprint-items
  if (t.status !== 'GEPLAND' || t.taakType === 'FOCUS') return 0;
  const n = Math.max(1, t.dagen || 1);
  return (t.urenPerDag ?? 0) * (n - 1) + (t.urenLaatsteDag ?? t.urenPerDag ?? 0);
}

export function geplandeUren(taken: Taak[]): number {
  return Math.round(taken.reduce((s, t) => s + urenVanTaak(t), 0) * 100) / 100;
}

/** SPEC §3.8 – status van een takenpakket. */
export function takenpakketVoortgang(tp: Takenpakket, alle: Takenpakket[], taken: Taak[], deadline: ISODate | null | undefined, vandaag: ISODate): PakketVoortgang {
  // SPEC §9: een backlog-item dat op Klaar staat is af, ook zonder ingeplande taken
  if (tp.status === 'KLAAR') return { status: 'Ingepland', label: 'Klaar', uren: tp.uren, ingepland: tp.uren };
  const ingepland = geplandeUren(taken.filter((t) => t.takenpakketId === tp.id));
  const vol = ingepland >= tp.uren - 0.01 && tp.uren > 0;
  const vorige = tp.afhankelijkVanId ? alle.find((x) => x.id === tp.afhankelijkVanId) : undefined;
  if (!vol && deadline && deadline < vandaag) return { status: 'Te laat', label: 'Te laat', uren: tp.uren, ingepland };
  if (vol) return { status: 'Ingepland', label: 'Ingepland', uren: tp.uren, ingepland };
  if (ingepland > 0) return { status: 'Bezig', label: 'Bezig', uren: tp.uren, ingepland };
  if (vorige) {
    const vu = geplandeUren(taken.filter((t) => t.takenpakketId === vorige.id));
    if (vu < vorige.uren - 0.01) return { status: 'Wacht', label: `Wacht op ${vorige.naam}`, uren: tp.uren, ingepland, wachtOp: vorige.id };
  }
  return { status: 'Open', label: 'Open', uren: tp.uren, ingepland };
}

export function werkpakketVoortgang(wp: Werkpakket, alleWp: Werkpakket[], tps: Takenpakket[], taken: Taak[], vandaag: ISODate): PakketVoortgang {
  const eigen = tps.filter((t) => t.werkpakketId === wp.id);
  const uren = eigen.reduce((s, t) => s + t.uren, 0);
  if (wp.werkwijze === 'AGILE') {
    // SPEC §9.2: voortgang = uren van items op Klaar
    const klaar = eigen.filter((t) => t.status === 'KLAAR').reduce((s, t) => s + t.uren, 0);
    const af = eigen.length > 0 && klaar >= uren - 0.01;
    if (af) return { status: 'Ingepland', label: 'Klaar', uren, ingepland: klaar };
    if (wp.deadline && wp.deadline < vandaag) return { status: 'Te laat', label: 'Te laat', uren, ingepland: klaar };
    if (eigen.some((t) => t.status && t.status !== 'BACKLOG')) return { status: 'Bezig', label: 'Bezig', uren, ingepland: klaar };
    return { status: 'Open', label: 'Open', uren, ingepland: klaar };
  }
  const ingepland = geplandeUren(taken.filter((t) => t.werkpakketId === wp.id || eigen.some((tp) => tp.id === t.takenpakketId)));
  const vol = uren > 0 && ingepland >= uren - 0.01;
  if (!vol && wp.deadline && wp.deadline < vandaag) return { status: 'Te laat', label: 'Te laat', uren, ingepland };
  if (vol) return { status: 'Ingepland', label: 'Ingepland', uren, ingepland };
  if (ingepland > 0) return { status: 'Bezig', label: 'Bezig', uren, ingepland };
  const vorige = wp.afhankelijkVanId ? alleWp.find((x) => x.id === wp.afhankelijkVanId) : undefined;
  if (vorige) {
    const v = werkpakketVoortgang(vorige, alleWp.filter((x) => x.id !== wp.id), tps, taken, vandaag);
    if (v.status !== 'Ingepland') return { status: 'Wacht', label: `Wacht op ${vorige.naam}`, uren, ingepland, wachtOp: vorige.id };
  }
  return { status: 'Open', label: 'Open', uren, ingepland };
}

/** Eerst mogelijke startdatum: dag na de laatste geplande taak van het vorige takenpakket of werkpakket. */
export function eerstMogelijk(opts: { vandaag: ISODate; takenpakket?: Takenpakket | null; werkpakket?: Werkpakket | null; taken: Taak[]; tps: Takenpakket[]; uitgezonderd?: string | null }): ISODate {
  const { vandaag, takenpakket, werkpakket, taken, tps, uitgezonderd } = opts;
  let st = vandaag;
  const laatste = (filter: (t: Taak) => boolean) =>
    taken.filter((t) => t.status === 'GEPLAND' && t.id !== uitgezonderd && filter(t) && t.einddatum).map((t) => t.einddatum!).sort().pop();
  const dagNa = (d?: string) => (d ? new Date(Date.parse(d + 'T00:00:00Z') + 86400000).toISOString().slice(0, 10) : undefined);
  if (takenpakket?.afhankelijkVanId) {
    const d = dagNa(laatste((t) => t.takenpakketId === takenpakket.afhankelijkVanId));
    if (d && d > st) st = d;
  }
  if (werkpakket?.afhankelijkVanId) {
    const vorigeTps = new Set(tps.filter((x) => x.werkpakketId === werkpakket.afhankelijkVanId).map((x) => x.id));
    const d = dagNa(laatste((t) => t.werkpakketId === werkpakket.afhankelijkVanId || (!!t.takenpakketId && vorigeTps.has(t.takenpakketId))));
    if (d && d > st) st = d;
  }
  return st;
}
